import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Loader2, PhoneCall, CalendarCheck, RefreshCw, Inbox } from "lucide-react";
import SEO from "@/components/SEO";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  online: "Online Consultation",
  clinic: "Clinic Appointment",
  "hiv-pep": "HIV PEP (Urgent)",
};

const TYPE_STYLES = {
  online: "bg-emerald-50 text-emerald-900 border-emerald-200",
  clinic: "bg-slate-100 text-slate-800 border-slate-300",
  "hiv-pep": "bg-amber-50 text-amber-900 border-amber-300",
};

const AdminDashboard = () => {
  const [admin, setAdmin] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/bookings`, { withCredentials: true });
      setBookings(data.bookings);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin");
      } else {
        toast.error("Could not load bookings.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAdmin(data);
        await load();
      } catch {
        navigate("/admin");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/admin");
  };

  const refresh = async () => {
    setLoading(true);
    await load();
    toast.success("Bookings refreshed");
  };

  return (
    <>
      <SEO title="Bookings Dashboard | Delhi STD Clinic" description="Private clinic bookings dashboard." path="/admin/dashboard" />
      <section className="min-h-[80vh] bg-[#FAF9F6] pt-28 pb-24 px-5 sm:px-8" data-testid="admin-dashboard">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-emerald-900/70 font-semibold">Private Dashboard</p>
              <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold text-slate-950">
                Appointment Bookings
              </h1>
              {admin && (
                <p className="mt-1 text-sm text-slate-500" data-testid="admin-user-label">
                  Signed in as {admin.email}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2.5">
              <button
                onClick={refresh}
                data-testid="admin-refresh-button"
                className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-800 text-[13px] font-semibold px-5 py-2.5 rounded-full hover:bg-slate-50 transition-all"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
              <button
                onClick={logout}
                data-testid="admin-logout-button"
                className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white text-[13px] font-semibold px-5 py-2.5 rounded-full transition-all"
              >
                <LogOut className="w-3.5 h-3.5" /> Log Out
              </button>
            </div>
          </div>

          {loading ? (
            <div className="mt-16 flex items-center justify-center gap-2 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading bookings…
            </div>
          ) : bookings && bookings.length > 0 ? (
            <div className="mt-8 space-y-4" data-testid="bookings-list">
              {bookings.map((b) => (
                <div
                  key={b.id}
                  data-testid={`booking-row-${b.reference}`}
                  className="bg-white border border-slate-900/10 rounded-3xl p-6 flex flex-col md:flex-row md:items-start gap-5"
                >
                  <div className="md:w-44 shrink-0">
                    <span className="font-mono text-sm font-bold text-emerald-950 bg-emerald-50 border border-emerald-900/15 rounded-lg px-3 py-1.5 inline-block">
                      {b.reference}
                    </span>
                    <p className="mt-2 text-xs text-slate-400">
                      {b.created_at
                        ? new Date(b.created_at).toLocaleString("en-IN", {
                            day: "numeric", month: "short", year: "numeric",
                            hour: "2-digit", minute: "2-digit",
                          })
                        : ""}
                    </p>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <p className="font-serif text-xl font-semibold text-slate-950">
                        {b.name} <span className="text-sm font-sans font-normal text-slate-500">· {b.age} yrs</span>
                      </p>
                      <span className={`text-[11px] font-bold tracking-wide uppercase border rounded-full px-3 py-1 ${TYPE_STYLES[b.consultation_type] || TYPE_STYLES.online}`}>
                        {TYPE_LABELS[b.consultation_type] || b.consultation_type}
                      </span>
                    </div>
                    {b.complaint && (
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                        <span className="font-medium text-slate-800">Concern:</span> {b.complaint}
                      </p>
                    )}
                    {(b.preferred_date || b.preferred_time) && (
                      <p className="mt-1.5 text-sm text-slate-500 inline-flex items-center gap-1.5">
                        <CalendarCheck className="w-3.5 h-3.5" />
                        Prefers {b.preferred_date || "any date"}{b.preferred_time ? ` at ${b.preferred_time}` : ""}
                      </p>
                    )}
                  </div>
                  <a
                    href={`tel:${b.contact_number}`}
                    data-testid={`booking-call-${b.reference}`}
                    className="shrink-0 inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white text-[13px] font-semibold px-5 py-2.5 rounded-full transition-all"
                  >
                    <PhoneCall className="w-3.5 h-3.5" /> {b.contact_number}
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-16 text-center" data-testid="bookings-empty">
              <Inbox className="w-10 h-10 mx-auto text-slate-300" />
              <p className="mt-4 font-serif text-2xl text-slate-700">No bookings yet</p>
              <p className="mt-2 text-sm text-slate-500">New appointment requests will appear here.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default AdminDashboard;
